/**
 * ExperienceSearch.js
 * 업무 경험 검색 페이지
 * 키워드 기반으로 최근 3개월간 해당 업무를 많이 한 직원을 찾는 기능
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Search, Clock, User } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import ReactionButton from '../components/ReactionButton';
import axios from 'axios';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.V1;

const ExperienceSearch = () => {
  const { colors, isDark } = useCustomTheme();
  const { isMobileView } = useMobileView();
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleSearch = async (value) => {
    if (!value || value.length < 2) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/networking/search`, {
        params: { keyword: value }
      });
      setResults(res.data || []);
    } catch (error) {
      console.error('검색 실패', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(keyword);
    }
  };

  return (
    <Box
      sx={{
        p: isMobileView ? 2 : 3,
        minHeight: '100vh',
        backgroundColor: colors.primaryBg,
      }}
    >
      {/* 검색 섹션 */}
      <Box sx={{ textAlign: 'center', mb: 4, maxWidth: '800px', mx: 'auto' }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 700,
            color: colors.primaryText,
            fontSize: isMobileView ? '1.5rem' : '20px',
            mb: 1,
          }}
        >
          🔍 사내 업무 경험 공유
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: colors.secondaryText,
            mb: 3,
            fontSize: isMobileView ? '0.875rem' : '0.9rem',
          }}
        >
          업체, 업무 관련 키워드로 관련 업무 경험을 공유해 보세요
        </Typography>
        <TextField
          fullWidth
          placeholder="키워드 입력 (예: 업체명, 계약명, 업무 내용)"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onKeyPress={handleKeyPress}
          size={isMobileView ? 'medium' : 'large'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} style={{ color: colors.secondaryText }} />
              </InputAdornment>
            ),
            endAdornment: loading && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: '500px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: colors.secondaryBg,
              '& fieldset': {
                borderColor: colors.primaryBorder,
              },
              '&:hover fieldset': {
                borderColor: colors.accent,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.accent,
              },
            },
            '& .MuiInputBase-input': {
              color: colors.primaryText,
            },
          }}
          onBlur={() => handleSearch(keyword)}
        />
      </Box>

      {/* 결과 섹션 */}
      <Box sx={{ maxWidth: '800px', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && results.length > 0 && (
          results.map((person) => (
            <Card
              key={person.employee_code}
              sx={{
                backgroundColor: colors.secondaryBg,
                border: `1px solid ${colors.primaryBorder}`,
                borderRadius: 2,
                position: 'relative',
                '&:hover': {
                  borderColor: colors.accent,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease',
                },
              }}
            >
              <CardContent sx={{ paddingBottom: '12px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexDirection: isMobileView ? 'column' : 'row' }}>
                  {/* 프로필 영역 */}
                  <Box sx={{ 
                    textAlign: 'center', 
                    minWidth: isMobileView ? '100%' : '100px',
                    mb: isMobileView ? 2 : 0,
                    paddingTop: isMobileView ? 0 : '20px',
                  }}>
                    <Avatar
                      sx={{
                        width: isMobileView ? 56 : 64,
                        height: isMobileView ? 56 : 64,
                        bgcolor: colors.accent,
                        mx: 'auto',
                        mb: 1,
                      }}
                    >
                      <User size={isMobileView ? 28 : 32} />
                    </Avatar>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: colors.primaryText,
                        fontSize: isMobileView ? '0.9rem' : '1rem',
                      }}
                    >
                      {person.employee_name}
                    </Typography>
                    {person.department && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.secondaryText,
                          fontSize: isMobileView ? '0.75rem' : '0.8rem',
                        }}
                      >
                        {person.department}
                      </Typography>
                    )}
                  </Box>

                  {/* 데이터 및 상세 내용 영역 */}
                  <Box sx={{ flex: 1, position: 'relative', minHeight: '120px', paddingTop: '20px', paddingBottom: person.recent_logs && person.recent_logs.length > 0 ? '28px' : '12px' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Chip
                        icon={<Clock size={14} />}
                        label={`최근 3개월 ${person.total_hours}시간 경험`}
                        size="small"
                        sx={{
                          backgroundColor: colors.accent,
                          color: '#fff',
                          '& .MuiChip-icon': {
                            color: '#fff',
                          },
                        }}
                      />
                      <Chip
                        label={`관련 로그 ${person.log_count}건`}
                        size="small"
                        sx={{
                          backgroundColor: colors.tertiaryBg,
                          color: colors.primaryText,
                        }}
                      />
                    </Box>

                    {/* 관련 업무 기록 */}
                    {person.recent_logs && person.recent_logs.length > 0 && (
                      <Box
                        sx={{
                          backgroundColor: colors.tertiaryBg,
                          p: 2,
                          borderRadius: 1,
                          border: `1px solid ${colors.primaryBorder}`,
                          marginBottom: '8px',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.secondaryText,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            display: 'block',
                            mb: 1,
                          }}
                        >
                          📋 관련 업무 기록 (발췌)
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2, listStyle: 'none' }}>
                          {person.recent_logs.map((log, idx) => (
                            <Box
                              component="li"
                              key={idx}
                              sx={{
                                color: colors.primaryText,
                                fontSize: '0.875rem',
                                lineHeight: 1.6,
                                mb: 0.5,
                                '&:last-child': { mb: 0 },
                              }}
                            >
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  color: colors.secondaryText,
                                  mr: 1,
                                  fontSize: '0.75rem',
                                }}
                              >
                                [{log.date}]
                              </Typography>
                              {log.description}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* 이모지 리액션 버튼 - 우측 하단 고정 */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                      }}
                    >
                      <ReactionButton
                        receiverCode={person.employee_code}
                        receiverName={person.employee_name}
                        initialCounts={person.reaction_counts || {}}
                        myReactions={person.my_reactions || []}
                        onReactionSuccess={(name, emoji) => {
                          setSnackbarMessage(`${name}님에게 ${emoji}를 보냈습니다.`);
                          setSnackbarOpen(true);
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        )}

        {!loading && hasSearched && results.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: colors.secondaryText,
            }}
          >
            <Typography variant="body2">
              검색 결과가 없습니다. 다른 키워드로 검색해보세요.
            </Typography>
          </Box>
        )}

        {!hasSearched && !loading && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: colors.secondaryText,
            }}
          >
            <Typography variant="body2">
              위 검색창에 키워드를 입력하여 검색해보세요.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Snackbar for reaction notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{
            backgroundColor: colors.accent,
            color: '#fff',
            '& .MuiAlert-icon': {
              color: '#fff',
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExperienceSearch;

